// src/dom/DesktopRenderer.ts
var RESIZE_EDGES = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];
var DesktopRenderer = class {
  manager;
  root;
  host;
  elements = /* @__PURE__ */ new Map();
  onRequestClose;
  onMount;
  cleanups = [];
  constructor(options) {
    this.manager = options.manager;
    this.root = options.root;
    this.onRequestClose = options.onRequestClose;
    this.onMount = options.onMount;
    this.host = document.createElement("div");
    this.host.className = "ex0-window-host";
    this.host.style.cssText = "position:relative;width:100%;height:100%;";
    this.root.appendChild(this.host);
    this.cleanups.push(
      this.manager.on("window:created", (e) => this.handleCreated(e)),
      this.manager.on("window:closed", (e) => this.handleClosed(e)),
      this.manager.on("state:changed", (e) => this.handleStateChanged(e))
    );
  }
  destroy() {
    for (const cleanup of this.cleanups) {
      cleanup();
    }
    this.cleanups.length = 0;
    for (const el of this.elements.values()) {
      el.container.remove();
    }
    this.elements.clear();
    this.host.remove();
  }
  handleCreated({
    windowId,
    state
  }) {
    const container = document.createElement("div");
    container.className = `ex0-window-container${state.focused ? " ex0-window--focused" : ""}`;
    container.style.cssText = `position:absolute;left:${state.position.x}px;top:${state.position.y}px;width:${state.size.width}px;height:${state.size.height}px;z-index:${state.zIndex};`;
    container.dataset.windowId = windowId;
    container.addEventListener("pointerdown", () => {
      this.manager.focus(windowId);
    });
    for (const edge of RESIZE_EDGES) {
      const handle = document.createElement("div");
      handle.className = `ex0-resize-handle ex0-resize-handle--${edge}`;
      handle.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.manager.startResize(windowId, edge, e);
      });
      container.appendChild(handle);
    }
    const frame = document.createElement("div");
    frame.className = "ex0-window";
    const titlebar = document.createElement("div");
    titlebar.className = "ex0-titlebar";
    titlebar.addEventListener("pointerdown", (e) => {
      if (!e.target.closest(".ex0-close-button")) {
        e.preventDefault();
        this.manager.startDrag(windowId, e);
      }
    });
    titlebar.addEventListener("dblclick", (e) => {
      if (e.target.closest(".ex0-close-button")) {
        return;
      }
      e.preventDefault();
      this.manager.toggleMaximize(windowId);
    });
    const buttonsLeft = document.createElement("div");
    buttonsLeft.className = "ex0-titlebar-buttons";
    const closeBtn = document.createElement("button");
    closeBtn.className = "ex0-close-button";
    closeBtn.type = "button";
    closeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.onRequestClose(windowId);
    });
    buttonsLeft.appendChild(closeBtn);
    const titleEl = document.createElement("div");
    titleEl.className = "ex0-title";
    titleEl.textContent = state.title;
    const buttonsRight = document.createElement("div");
    buttonsRight.className = "ex0-titlebar-buttons ex0-titlebar-buttons--right";
    titlebar.appendChild(buttonsLeft);
    titlebar.appendChild(titleEl);
    titlebar.appendChild(buttonsRight);
    frame.appendChild(titlebar);
    const content = document.createElement("div");
    content.className = "ex0-content";
    const contentInner = document.createElement("div");
    contentInner.className = "ex0-content-inner";
    content.appendChild(contentInner);
    frame.appendChild(content);
    container.appendChild(frame);
    this.host.appendChild(container);
    this.elements.set(windowId, {
      container,
      titleEl,
      contentInner,
      prevPosition: { ...state.position },
      prevSize: { ...state.size },
      prevZIndex: state.zIndex,
      prevFocused: state.focused,
      prevTitle: state.title,
      prevMinimized: state.minimized,
      prevMaximized: state.maximized
    });
    this.onMount(windowId, contentInner);
  }
  handleClosed({ windowId }) {
    const el = this.elements.get(windowId);
    if (el) {
      el.container.remove();
      this.elements.delete(windowId);
    }
  }
  handleStateChanged({
    state
  }) {
    for (const [id, el] of this.elements) {
      const win = state.windows.get(id);
      if (!win) {
        continue;
      }
      if (win.position.x !== el.prevPosition.x || win.position.y !== el.prevPosition.y) {
        el.container.style.left = `${win.position.x}px`;
        el.container.style.top = `${win.position.y}px`;
        el.prevPosition = { ...win.position };
      }
      if (win.size.width !== el.prevSize.width || win.size.height !== el.prevSize.height) {
        el.container.style.width = `${win.size.width}px`;
        el.container.style.height = `${win.size.height}px`;
        el.prevSize = { ...win.size };
      }
      if (win.zIndex !== el.prevZIndex) {
        el.container.style.zIndex = `${win.zIndex}`;
        el.prevZIndex = win.zIndex;
      }
      if (win.focused !== el.prevFocused) {
        el.container.classList.toggle("ex0-window--focused", win.focused);
        el.prevFocused = win.focused;
      }
      if (win.title !== el.prevTitle) {
        el.titleEl.textContent = win.title;
        el.prevTitle = win.title;
      }
      if (win.minimized !== el.prevMinimized) {
        el.container.style.display = win.minimized ? "none" : "";
        el.prevMinimized = win.minimized;
      }
      if (win.maximized !== el.prevMaximized) {
        el.container.classList.toggle("ex0-window--maximized", win.maximized);
        el.prevMaximized = win.maximized;
      }
    }
  }
};
function createDesktopRenderer(options) {
  return new DesktopRenderer(options);
}
export {
  DesktopRenderer,
  createDesktopRenderer
};
